import { inject, Injectable, Injector, type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, map, merge, of, startWith, Subject, switchMap } from 'rxjs';

import { DesignChangePresenceService, DesignChangeRepository } from '@efficiently/client-api-design-changes';
import { ProjectDetailSelectors } from '@efficiently/client-domain-project-detail';

@Injectable()
export class DefaultDesignChangePresenceService extends DesignChangePresenceService {
	private readonly repository = inject(DesignChangeRepository);
	private readonly refreshSubject = new Subject<void>();

	private readonly store = inject(Store);
	private readonly injector = inject(Injector);

	/**
	 * Declared `string` by the selector, but the state carries no default — outside a loaded project it
	 * reads `undefined`. That is why every consumer below tests it rather than trusting the type.
	 */
	private readonly projectId: Signal<string> = this.store.selectSignal(ProjectDetailSelectors.projectId);

	private readonly projectId$ = merge(
		of(this.projectId()),
		toObservable(this.projectId, { injector: this.injector }),
	).pipe(distinctUntilChanged());

	/**
	 * `projectDesignChanges` is project-scoped, so with no project there is nothing to ask and the read
	 * is skipped — it used to fire anyway, twice over: once at construction, when `projectId` is still
	 * `undefined`, and again on every navigation OUT of a project, because leaving one is a distinct
	 * change like any other. Both landed as `Apollo.Error: projectDesignChanges` in the console on the
	 * projects list. `skipGlobalErrorHandler` does not cover that — its contract is "no snackbar,
	 * logging is still expected" — so the only fix is not making the request.
	 *
	 * The refresh is mapped through the current project for the same reason: a refresh with no project
	 * would otherwise walk straight past the guard.
	 */
	public override readonly hasDesignChanges: Signal<boolean> = toSignal(
		merge(this.projectId$, this.refreshSubject.pipe(map(() => this.projectId()))).pipe(
			switchMap(projectId =>
				projectId ? this.repository.hasDesignChanges().pipe(startWith(false)) : of(false),
			),
		),
		{ initialValue: false },
	);

	public override refresh(): void {
		this.refreshSubject.next();
	}
}
